import { Search, TrendingUp, Zap, ArrowRight, Sparkles } from "lucide-react";
import ecosystemAsset from "@/assets/orbitrank-ecosystem.jpeg.asset.json";

const scrollToHow = () => {
  document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
};

const cards = [
  {
    icon: Search,
    title: "Get Found",
    desc: "Increase visibility across search engines, AI platforms, and maps.",
    accent: "#00FF88",
  },
  {
    icon: TrendingUp,
    title: "Get More Leads",
    desc: "Convert more visitors into calls, forms, and paying customers.",
    accent: "#3B82F6",
  },
  {
    icon: Zap,
    title: "Save Time",
    desc: "AI analyzes, optimizes, and monitors everything automatically.",
    accent: "#A78BFA",
  },
];

const OrbitVisual = () => (
  <div className="ge-orbit-wrap">
    <img
      src={ecosystemAsset.url}
      alt="OrbitRank AI SEO ecosystem connecting Google Search, Google Analytics 4, Google Search Console, Google Ads, Keyword Research, Competitor Analysis, Backlink Monitor, Technical SEO, Rank Tracking, and Content Optimizer"
      style={{ width: "100%", height: "auto", display: "block" }}
      loading="lazy"
    />
  </div>
);

const GrowthEngineSection = () => {
  return (
    <section className="ge-section">
      <div className="ge-container">
        <div className="ge-grid">
          <div className="ge-left">
            <div className="ge-badge">
              <Sparkles size={12} strokeWidth={2.4} />
              <span>BUILT FOR THE NEW SEARCH ERA</span>
            </div>
            <h2 className="ge-headline">
              Turn your website into a{" "}
              <span className="ge-grad">growth engine.</span>
            </h2>
            <p className="ge-desc">
              Orbit Rank helps businesses get discovered across search engines, AI platforms,
              and local channels — while driving more leads, customers, and growth.
            </p>
          </div>

          <div className="ge-right">
            <OrbitVisual />
          </div>
        </div>

        <div className="ge-cards">
          {cards.map((c) => (
            <div key={c.title} className="ge-card" style={{ ["--accent" as any]: c.accent }}>
              <div className="ge-card-icon">
                <c.icon size={20} strokeWidth={2} />
              </div>
              <div className="ge-card-bar" />
              <h3 className="ge-card-title">{c.title}</h3>
              <p className="ge-card-desc">{c.desc}</p>
              <div className="ge-card-arrow">
                <ArrowRight size={16} strokeWidth={2} />
              </div>
            </div>
          ))}
        </div>

        <button type="button" onClick={scrollToHow} className="ge-cta">
          <div className="ge-cta-icon">
            <Sparkles size={18} strokeWidth={2} />
          </div>
          <div className="ge-cta-text">
            <div className="ge-cta-title">See how Orbit Rank works</div>
            <div className="ge-cta-sub">AI-powered SEO. Smarter insights. Real growth.</div>
          </div>
          <div className="ge-cta-arrow">
            <ArrowRight size={18} strokeWidth={2} />
          </div>
        </button>
      </div>

      <style>{`
        .ge-section {
          position: relative;
          background: #000;
          padding: 64px 20px 72px;
          overflow: hidden;
        }
        .ge-container { max-width: 1200px; margin: 0 auto; }
        .ge-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 32px;
          align-items: center;
        }
        @media (min-width: 900px) {
          .ge-section { padding: 120px 32px; }
          .ge-grid { grid-template-columns: 1.05fr 1fr; gap: 64px; }
        }
        .ge-left { animation: geFadeUp 0.9s ease both; }
        .ge-right { animation: geFadeUp 1s ease both 0.15s; }

        .ge-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          border: 1px solid rgba(0,255,136,0.25);
          background: rgba(0,255,136,0.04);
          color: #00FF88;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.18em;
          margin-bottom: 24px;
          box-shadow: 0 0 24px rgba(0,255,136,0.06) inset;
        }
        .ge-headline {
          margin: 0 0 20px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: clamp(34px, 5.4vw, 60px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.025em;
          color: #FFFFFF;
        }
        .ge-grad {
          background: linear-gradient(90deg, #00FF88 0%, #3B82F6 55%, #A78BFA 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .ge-desc {
          margin: 0;
          color: #9CA3AF;
          font-size: 15.5px;
          line-height: 1.65;
          max-width: 480px;
        }

        /* Ecosystem visual */
        .ge-orbit-wrap {
          position: relative;
          width: 100%;
          max-width: 560px;
          margin: 0 auto;
        }

        @keyframes geFadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: none; }
        }

        /* Cards */
        .ge-cards {
          margin-top: 56px;
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        @media (min-width: 760px) {
          .ge-cards { grid-template-columns: repeat(3, 1fr); gap: 20px; }
        }
        .ge-card {
          position: relative;
          padding: 26px 22px 22px;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.07);
          background:
            linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.005) 100%),
            #050608;
          backdrop-filter: blur(10px);
          transition: transform .35s ease, border-color .35s ease, box-shadow .35s ease;
          overflow: hidden;
        }
        .ge-card::before {
          content: "";
          position: absolute; inset: 0;
          border-radius: 18px;
          background: radial-gradient(circle at 20% 0%, var(--accent) 0%, transparent 60%);
          opacity: 0.06;
          pointer-events: none;
        }
        .ge-card:hover {
          transform: translateY(-4px);
          border-color: color-mix(in srgb, var(--accent) 35%, transparent);
          box-shadow: 0 18px 50px -20px color-mix(in srgb, var(--accent) 40%, transparent);
        }
        .ge-card-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--accent);
          background: color-mix(in srgb, var(--accent) 10%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
          margin-bottom: 22px;
        }
        .ge-card-bar {
          width: 28px; height: 2px; border-radius: 2px;
          background: var(--accent);
          opacity: 0.85;
          margin-bottom: 14px;
        }
        .ge-card-title {
          margin: 0 0 8px;
          font-size: 19px;
          font-weight: 800;
          color: #FFFFFF;
          letter-spacing: -0.01em;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .ge-card-desc {
          margin: 0 0 28px;
          color: #9CA3AF;
          font-size: 14px;
          line-height: 1.6;
        }
        .ge-card-arrow {
          width: 36px; height: 36px; border-radius: 999px;
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--accent);
          border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
          background: color-mix(in srgb, var(--accent) 6%, transparent);
          transition: transform .3s ease, background .3s ease;
        }
        .ge-card:hover .ge-card-arrow {
          transform: translateX(4px);
          background: color-mix(in srgb, var(--accent) 14%, transparent);
        }

        /* CTA strip */
        .ge-cta {
          margin-top: 28px;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 22px 22px;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.08);
          background:
            linear-gradient(90deg, rgba(0,255,136,0.05) 0%, rgba(59,130,246,0.04) 50%, rgba(167,139,250,0.05) 100%),
            #050608;
          color: #FFFFFF;
          text-align: left;
          cursor: pointer;
          transition: transform .3s ease, border-color .3s ease, box-shadow .3s ease;
          box-shadow: 0 0 0 1px rgba(0,255,136,0.04), 0 20px 60px -30px rgba(0,255,136,0.25);
        }
        .ge-cta:hover {
          transform: translateY(-2px);
          border-color: rgba(0,255,136,0.3);
          box-shadow: 0 0 0 1px rgba(0,255,136,0.12), 0 30px 70px -25px rgba(0,255,136,0.35);
        }
        .ge-cta-icon {
          width: 46px; height: 46px; flex-shrink: 0;
          border-radius: 999px;
          display: inline-flex; align-items: center; justify-content: center;
          color: #00FF88;
          background: rgba(0,255,136,0.08);
          border: 1px solid rgba(0,255,136,0.3);
        }
        .ge-cta-text { flex: 1; min-width: 0; }
        .ge-cta-title {
          font-size: 17px; font-weight: 800; letter-spacing: -0.01em;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .ge-cta-sub {
          margin-top: 4px;
          font-size: 13px; color: #9CA3AF;
        }
        .ge-cta-arrow {
          width: 44px; height: 44px; flex-shrink: 0;
          border-radius: 999px;
          display: inline-flex; align-items: center; justify-content: center;
          color: #FFFFFF;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.02);
          transition: transform .3s ease, border-color .3s ease;
        }
        .ge-cta:hover .ge-cta-arrow {
          transform: translateX(4px);
          border-color: rgba(0,255,136,0.5);
        }
      `}</style>
    </section>
  );
};

export default GrowthEngineSection;
